<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { isValidPhoneNumber } from "libphonenumber-js";

const props = defineProps<{
  show: boolean;
  totalAmount: number;
  ticketCount: number;
  loading: boolean;
  email?: string;
  exchangeRate?: number;
  exchangeDate?: string;
}>();

const emit = defineEmits(["close", "confirm"]);

// --- COUNTRIES LIST (Americas + Europe) ---
const COUNTRIES = [
  // Americas
  { code: "VE", name: "🇻🇪 Venezuela", phoneCode: "+58", docLabel: "Cédula (V-12345678)", docPlaceholder: "V-12345678", docRegex: /^[VEJP]-\d{5,9}$/ },
  { code: "CO", name: "🇨🇴 Colombia", phoneCode: "+57", docLabel: "Cédula de Ciudadanía", docPlaceholder: "1234567890", docRegex: /^\d{6,12}$/ },
  { code: "US", name: "🇺🇸 Estados Unidos", phoneCode: "+1", docLabel: "SSN / ID", docPlaceholder: "123-45-6789", docRegex: /^.{5,}$/ },
  { code: "MX", name: "🇲🇽 México", phoneCode: "+52", docLabel: "CURP / INE", docPlaceholder: "CURP o INE", docRegex: /^.{10,}$/ },
  { code: "AR", name: "🇦🇷 Argentina", phoneCode: "+54", docLabel: "DNI", docPlaceholder: "12345678", docRegex: /^\d{7,8}$/ },
  { code: "CL", name: "🇨🇱 Chile", phoneCode: "+56", docLabel: "RUT", docPlaceholder: "12345678-9", docRegex: /^[\d\-kK]{8,12}$/ },
  { code: "PE", name: "🇵🇪 Perú", phoneCode: "+51", docLabel: "DNI", docPlaceholder: "12345678", docRegex: /^\d{8}$/ },
  { code: "EC", name: "🇪🇨 Ecuador", phoneCode: "+593", docLabel: "Cédula", docPlaceholder: "1234567890", docRegex: /^\d{10}$/ },
  { code: "BR", name: "🇧🇷 Brasil", phoneCode: "+55", docLabel: "CPF", docPlaceholder: "123.456.789-00", docRegex: /^[\d\.\-]{11,14}$/ },
  { code: "PA", name: "🇵🇦 Panamá", phoneCode: "+507", docLabel: "Cédula", docPlaceholder: "8-123-4567", docRegex: /^.{5,}$/ },
  { code: "DO", name: "🇩🇴 Rep. Dominicana", phoneCode: "+1", docLabel: "Cédula", docPlaceholder: "001-1234567-8", docRegex: /^.{10,}$/ },
  { code: "CR", name: "🇨🇷 Costa Rica", phoneCode: "+506", docLabel: "Cédula", docPlaceholder: "1-2345-6789", docRegex: /^.{8,}$/ },
  { code: "GT", name: "🇬🇹 Guatemala", phoneCode: "+502", docLabel: "DPI", docPlaceholder: "1234 56789 1234", docRegex: /^.{10,}$/ },
  // Europe
  { code: "ES", name: "🇪🇸 España", phoneCode: "+34", docLabel: "DNI / NIE", docPlaceholder: "12345678A", docRegex: /^[\dA-Z]{8,12}$/ },
  { code: "PT", name: "🇵🇹 Portugal", phoneCode: "+351", docLabel: "NIF / CC", docPlaceholder: "123456789", docRegex: /^\d{9}$/ },
  { code: "IT", name: "🇮🇹 Italia", phoneCode: "+39", docLabel: "Codice Fiscale", docPlaceholder: "RSSMRA85M01H501Z", docRegex: /^[A-Z0-9]{16}$/ },
  { code: "FR", name: "🇫🇷 Francia", phoneCode: "+33", docLabel: "Carte d'Identité", docPlaceholder: "ABC123456", docRegex: /^.{8,}$/ },
  { code: "DE", name: "🇩🇪 Alemania", phoneCode: "+49", docLabel: "Personalausweis", docPlaceholder: "L12345678", docRegex: /^.{8,}$/ },
  { code: "GB", name: "🇬🇧 Reino Unido", phoneCode: "+44", docLabel: "Passport / NI", docPlaceholder: "AB123456C", docRegex: /^.{6,}$/ },
  { code: "NL", name: "🇳🇱 Países Bajos", phoneCode: "+31", docLabel: "BSN", docPlaceholder: "123456789", docRegex: /^\d{9}$/ },
];

const form = ref({
  full_name: "",
  country: "VE",
  document_id: "",
  email: "",
  phone: "",
  phone_country: "VE",
  payment_method: "PAGO_MOVIL",
  payment_ref: "",
  payment_source_email: "",
  payment_issuing_bank: "",
  payment_source_number: "",
  amount_bs: 0 as number | null,
  payment_exact_date: new Date().toISOString().split("T")[0],
});

// Get current country config
const currentCountry = computed(() => {
  return COUNTRIES.find(c => c.code === form.value.country) || COUNTRIES[0];
});

const phoneCountry = computed(() => {
  return COUNTRIES.find(c => c.code === form.value.phone_country) || COUNTRIES[0];
});

// Update Bs Amount when total changes or rate is available
watch(
  () => [props.totalAmount, props.exchangeRate],
  () => {
    if (props.exchangeRate) {
      form.value.amount_bs = parseFloat(
        (props.totalAmount * props.exchangeRate).toFixed(2)
      );
    }
  },
  { immediate: true }
);

// Sync phone country with document country
watch(() => form.value.country, (newCountry) => {
  form.value.phone_country = newCountry;
  form.value.document_id = ""; // Reset document when country changes
});

// Copy Helper
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error("Failed to copy", err);
  }
};

// Force Uppercase on inputs
const toUpper = (field: keyof typeof form.value) => {
  const val = form.value[field];
  if (typeof val === "string") {
    // @ts-ignore
    form.value[field] = val.toUpperCase();
  }
};

watch(
  () => props.show,
  (newVal) => {
    if (newVal && props.email) {
      form.value.email = props.email;
    }
  }
);

// --- STATIC DATA: BANKS ---
const VENEZUELA_BANKS = [
  "0102 - BANCO DE VENEZUELA",
  "0105 - MERCANTIL",
  "0108 - PROVINCIAL",
  "0114 - BANCARIBE",
  "0115 - EXTERIOR",
  "0128 - BANCO CARONI",
  "0134 - BANESCO",
  "0138 - BANCO PLAZA",
  "0151 - BFC FONDO COMUN",
  "0156 - 100% BANCO",
  "0157 - DEL SUR",
  "0163 - BANCO DEL TESORO",
  "0166 - BANCO AGRICOLA",
  "0168 - BANCRECER",
  "0169 - MI BANCO",
  "0171 - BANCO ACTIVO",
  "0172 - BANCAMIGA",
  "0174 - BANPLUS",
  "0175 - BICENTENARIO",
  "0177 - BANFANB",
  "0191 - BNC NACIONAL DE CREDITO",
];

const COLOMBIA_BANKS = [
  "BANCOLOMBIA",
  "NEQUI",
  "DAVIPLATA",
  "BANCO DE BOGOTA",
  "BANCO DAVIVIENDA",
  "BBVA COLOMBIA",
  "BANCO DE OCCIDENTE",
  "BANCO POPULAR",
  "BANCO GNB SUDAMERIS",
  "SCOTIABANK COLPATRIA",
  "BANCO AV VILLAS",
  "BANCO CAJA SOCIAL",
  "BANCO FALABELLA",
  "BANCO PICHINCHA",
  "BANCOOMEVA",
  "BANCO SANTANDER",
  "CITIBANK",
];

// --- VALIDATION LOGIC ---

const isDocumentValid = computed(() => {
  const country = currentCountry.value;
  if (country.docRegex) {
    return country.docRegex.test(form.value.document_id);
  }
  return form.value.document_id.length > 5;
});

const isPhoneValid = computed(() => {
  try {
    const fullPhone = phoneCountry.value.phoneCode + form.value.phone.replace(/^0+/, '');
    return isValidPhoneNumber(fullPhone, form.value.phone_country as any);
  } catch {
    // Fallback for invalid format
    return form.value.phone.length >= 7;
  }
});

const phoneError = computed(() => {
  if (form.value.phone.length < 4) return "";
  if (!isPhoneValid.value) {
    return `Formato inválido para ${phoneCountry.value.name}`;
  }
  return "";
});

const isPaymentRefValid = computed(() => {
  if (form.value.payment_method === "CASH") return true;
  return form.value.payment_ref.length >= 4;
});

const isDynamicFieldsValid = computed(() => {
  const method = form.value.payment_method;

  if (["ZELLE", "BINANCE"].includes(method)) {
    return form.value.payment_source_email.includes("@");
  }

  if (method === "BIZUM") {
    return form.value.payment_source_number.length > 5;
  }

  if (["PAGO_MOVIL", "BS_TRANSFER"].includes(method)) {
    const bankValid = form.value.payment_issuing_bank.length > 2;
    const amountValid = (form.value.amount_bs || 0) > 0;
    const dateValid = (form.value.payment_exact_date?.length || 0) > 0;
    return bankValid && amountValid && dateValid;
  }

  if (["BANCOLOMBIA", "NEQUI"].includes(method)) {
    return form.value.payment_issuing_bank.length > 2;
  }

  return true;
});

const isFormValid = computed(() => {
  return (
    form.value.full_name.length > 3 &&
    isDocumentValid.value &&
    form.value.email.includes("@") &&
    isPhoneValid.value &&
    isPaymentRefValid.value &&
    isDynamicFieldsValid.value
  );
});

const handleSubmit = () => {
  if (!isFormValid.value) return;
  // Add full phone with country code
  const fullPhone = phoneCountry.value.phoneCode + form.value.phone.replace(/^0+/, '');
  emit("confirm", { ...form.value, phone_full: fullPhone });
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-4"
  >
    <!-- Backdrop -->
    <div
      class="absolute inset-0 bg-black/80 backdrop-blur-sm"
      @click="!loading && emit('close')"
    ></div>

    <!-- Modal -->
    <div
      class="relative bg-surface w-full max-w-lg rounded-2xl border border-white/10 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]"
    >
      <!-- Header -->
      <div
        class="p-6 border-b border-white/5 bg-linear-to-r from-surface to-white/5 flex justify-between items-center"
      >
        <div>
          <h3 class="text-xl font-bold text-white">Finalizar Compra</h3>
          <p class="text-sm text-gray-400 mt-1">
            Estás reservando
            <span class="text-gold-400 font-bold"
              >{{ ticketCount }} tickets</span
            >
          </p>
        </div>
        <div class="text-right">
          <span class="text-xs text-gray-500 uppercase font-bold"
            >Total a Pagar</span
          >
          <div class="text-2xl font-black text-white">
            🥬{{
              totalAmount.toLocaleString("es-VE", { minimumFractionDigits: 2 })
            }}
          </div>
        </div>
      </div>

      <!-- Body (Scrollable) -->
      <div class="p-6 space-y-5 overflow-y-auto custom-scrollbar">
        <!-- Personal Data Section -->
        <div class="space-y-3">
          <h4
            class="text-xs font-bold text-gold-500 uppercase tracking-widest mb-2 border-b border-white/10 pb-1"
          >
            Datos Personales
          </h4>

          <!-- Name -->
          <div>
            <label class="block text-xs font-bold text-gray-500 uppercase mb-1"
              >Nombre Completo</label
            >
            <input
              v-model="form.full_name"
              @input="toUpper('full_name')"
              type="text"
              placeholder="APELLIDOS Y NOMBRES"
              class="input-field uppercase"
            />
          </div>

          <!-- Country & ID -->
          <div class="grid grid-cols-3 gap-3">
            <div class="col-span-1">
              <label
                class="block text-xs font-bold text-gray-500 uppercase mb-1"
                >País</label
              >
              <select
                v-model="form.country"
                class="input-field appearance-none bg-white !text-black border-white text-xs"
              >
                <option v-for="country in COUNTRIES" :key="country.code" :value="country.code">
                  {{ country.name }}
                </option>
              </select>
            </div>
            <div class="col-span-2">
              <label
                class="block text-xs font-bold text-gray-500 uppercase mb-1"
              >
                {{ currentCountry?.docLabel || 'Documento ID' }}
              </label>
              <input
                v-model="form.document_id"
                @input="toUpper('document_id')"
                type="text"
                :placeholder="currentCountry?.docPlaceholder || 'ID'"
                class="input-field uppercase"
                :class="{
                  '!border-red-500/50':
                    form.document_id.length > 0 && !isDocumentValid,
                }"
              />
              <p
                v-if="form.document_id.length > 0 && !isDocumentValid"
                class="text-[10px] text-red-400 mt-1"
              >
                Formato inválido para {{ currentCountry?.name }}
              </p>
            </div>
          </div>

          <!-- Contact -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label
                class="block text-xs font-bold text-gray-500 uppercase mb-1"
                >Teléfono</label
              >
              <div class="flex gap-1">
                <select
                  v-model="form.phone_country"
                  class="input-field !w-24 appearance-none bg-white !text-black text-xs !px-2"
                >
                  <option v-for="c in COUNTRIES" :key="c.code" :value="c.code">
                    {{ c.phoneCode }}
                  </option>
                </select>
                <input
                  v-model="form.phone"
                  type="tel"
                  placeholder="4121234567"
                  class="input-field flex-1"
                  :class="{
                    '!border-red-500/50': form.phone.length > 3 && !isPhoneValid,
                  }"
                />
              </div>
              <p
                v-if="form.phone.length > 3 && !isPhoneValid"
                class="text-[10px] text-red-400 mt-1"
              >
                {{ phoneError || 'Número inválido' }}
              </p>
            </div>
            <div>
              <label
                class="block text-xs font-bold text-gray-500 uppercase mb-1"
                >Email</label
              >
              <input
                v-model="form.email"
                type="email"
                placeholder="correo@ejemplo.com"
                class="input-field lowercase"
              />
            </div>
          </div>
        </div>

        <!-- Payment Section -->
        <div class="space-y-3 pt-2">
          <h4
            class="text-xs font-bold text-gold-500 uppercase tracking-widest mb-2 border-b border-white/10 pb-1"
          >
            Método de Pago
          </h4>

          <div class="grid grid-cols-3 gap-2">
            <button
              v-for="method in [
                {
                  id: 'PAGO_MOVIL',
                  label: 'Pago Móvil',
                  class: 'border-gold-500 text-gold-500',
                  activeClass: 'bg-gold-500 !text-black',
                },
                {
                  id: 'BS_TRANSFER',
                  label: 'Transf. Bs',
                  class: 'border-blue-400 text-blue-400',
                  activeClass: 'bg-blue-400 !text-white',
                },
                {
                  id: 'ZELLE',
                  label: 'Zelle',
                  class: 'border-purple-500 text-purple-500',
                  activeClass: 'bg-purple-500 !text-white',
                },
                {
                  id: 'BANCOLOMBIA',
                  label: 'Bancolombia',
                  class: 'border-red-500 text-red-500',
                  activeClass: 'bg-red-500 !text-white',
                },
                {
                  id: 'NEQUI',
                  label: 'Nequi',
                  class: 'border-pink-500 text-pink-500',
                  activeClass: 'bg-pink-500 !text-white',
                },
                {
                  id: 'BIZUM',
                  label: 'Bizum',
                  class: 'border-cyan-500 text-cyan-500',
                  activeClass: 'bg-cyan-500 !text-white',
                },
                {
                  id: 'BINANCE',
                  label: 'Binance',
                  class: 'border-yellow-500 text-yellow-500',
                  activeClass: 'bg-yellow-500 !text-black',
                },
                {
                  id: 'CASH',
                  label: 'Efectivo',
                  class: 'border-green-500 text-green-500',
                  activeClass: 'bg-green-500 !text-white',
                },
              ]"
              :key="method.id"
              @click="form.payment_method = method.id"
              type="button"
              class="px-2 py-3 rounded-lg border text-[10px] font-bold transition-all text-center hover:bg-white/5 uppercase break-words leading-tight flex items-center justify-center min-h-[50px]"
              :class="
                form.payment_method === method.id
                  ? method.activeClass
                  : 'bg-transparent border-white/10 text-gray-400'
              "
            >
              {{ method.label }}
            </button>
          </div>

          <!-- Dynamic Payment Info Box -->
          <div
            class="bg-black/30 rounded-xl p-4 border border-white/5 text-sm space-y-4 animate-in fade-in slide-in-from-top-2 relative overflow-hidden"
          >
            <!-- Watermark/bg hint -->
            <div
              class="absolute -right-4 -top-4 text-white/5 text-6xl font-black select-none pointer-events-none icon-bg"
            >
              {{
                form.payment_method === "PAGO_MOVIL"
                  ? "📱"
                  : form.payment_method === "ZELLE"
                    ? "💵"
                    : "💳"
              }}
            </div>

            <!-- Bs Amount is calculated automatically but rate is not shown to public -->

            <!-- INSTRUCTIONS with Copy Buttons & Titular Highlight -->
            <div v-if="form.payment_method === 'PAGO_MOVIL'">
              <p class="text-xs text-gold-400 font-bold uppercase mb-1">
                Datos Pago Móvil:
              </p>
              <div class="font-mono text-white text-xs space-y-1 relative z-10">
                <p class="flex items-center gap-2">
                  Banco: <span class="text-gray-300">0134 - BANESCO</span>
                  <button
                    @click.stop="copyToClipboard('0134')"
                    class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p class="flex items-center gap-2">
                  Teléfono: <span class="text-gray-300">0426-275 02 25</span>
                  <button
                    @click.stop="copyToClipboard('04262750225')"
                    class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p class="flex items-center gap-2">
                  Cédula: <span class="text-gray-300">13.831.960</span>
                  <button
                    @click.stop="copyToClipboard('13831960')"
                    class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p
                  class="text-xs mt-1 font-bold text-yellow-400 bg-yellow-400/10 p-1 rounded inline-block"
                >
                  Titular: ARGENIS TORRES
                </p>
              </div>
            </div>

            <div v-if="form.payment_method === 'BS_TRANSFER'">
              <p class="text-xs text-blue-400 font-bold uppercase mb-1">
                Transferencia Banesco:
              </p>
              <div class="font-mono text-white text-xs space-y-1 relative z-10">
                <p class="flex items-center gap-2">
                  Cuenta:
                  <span class="text-gray-300 text-[10px] break-all"
                    >0134-0476-31-4761028552</span
                  >
                  <button
                    @click.stop="copyToClipboard('01340476314761028552')"
                    class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p class="flex items-center gap-2">
                  Cédula: <span class="text-gray-300">13.831.960</span>
                  <button
                    @click.stop="copyToClipboard('13831960')"
                    class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p
                  class="text-xs mt-1 font-bold text-yellow-400 bg-yellow-400/10 p-1 rounded inline-block"
                >
                  Titular: ARGENIS TORRES
                </p>
              </div>
            </div>

            <div v-else-if="form.payment_method === 'ZELLE'">
              <p class="text-xs text-purple-400 font-bold uppercase mb-1">
                Datos Zelle:
              </p>
              <div class="font-mono text-white text-xs space-y-1 relative z-10">
                <p class="flex items-center gap-2">
                  Email:
                  <span class="text-gray-300 break-all"
                    >genesiscarrillog9@gmail.com</span
                  >
                  <button
                    @click.stop="
                      copyToClipboard('genesiscarrillog9@gmail.com')
                    "
                    class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p
                  class="text-xs mt-1 font-bold text-yellow-400 bg-yellow-400/10 p-1 rounded inline-block"
                >
                  Titular: Génesis Carrillo
                </p>
              </div>
            </div>

            <div v-else-if="form.payment_method === 'BANCOLOMBIA'">
              <p class="text-xs text-red-400 font-bold uppercase mb-1">
                Datos Bancolombia:
              </p>
              <div class="font-mono text-white text-xs space-y-1 relative z-10">
                <p class="flex items-center gap-2">
                  Ahorros: <span class="text-gray-300">869000004018</span>
                  <button
                    @click.stop="copyToClipboard('869000004018')"
                    class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p class="flex items-center gap-2">
                  C.C.: <span class="text-gray-300">5001358654</span>
                  <button
                    @click.stop="copyToClipboard('5001358654')"
                    class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p
                  class="text-xs mt-1 font-bold text-yellow-400 bg-yellow-400/10 p-1 rounded inline-block"
                >
                  Titular: JOGRELYS TORRES
                </p>
              </div>
            </div>

            <div v-else-if="form.payment_method === 'NEQUI'">
              <p class="text-xs text-pink-400 font-bold uppercase mb-1">
                Datos Nequi:
              </p>
              <div class="font-mono text-white text-xs space-y-1 relative z-10">
                <p class="flex items-center gap-2 text-xl font-bold">
                  321 395 8081
                  <button
                    @click.stop="copyToClipboard('3213958081')"
                    class="text-xs bg-white/10 px-2 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p
                  class="text-xs mt-1 font-bold text-yellow-400 bg-yellow-400/10 p-1 rounded inline-block"
                >
                  Titular: JOGRELYS TORRES
                </p>
              </div>
            </div>

            <div v-else-if="form.payment_method === 'BINANCE'">
              <p class="text-xs text-yellow-400 font-bold uppercase mb-1">
                Binance Pay:
              </p>
              <p
                class="font-mono text-white text-sm break-all flex items-center gap-2"
              >
                diegobinance11@gmail.com
                <button
                  @click.stop="copyToClipboard('diegobinance11@gmail.com')"
                  class="text-[10px] bg-white/10 px-1 rounded hover:bg-white/20"
                >
                  COPIAR
                </button>
              </p>
            </div>

            <div v-else-if="form.payment_method === 'BIZUM'">
              <p class="text-xs text-cyan-400 font-bold uppercase mb-1">
                Datos Bizum:
              </p>
              <div class="font-mono text-white text-xs space-y-1 relative z-10">
                <p class="flex items-center gap-2 text-xl font-bold">
                  656451082
                  <button
                    @click.stop="copyToClipboard('656451082')"
                    class="text-xs bg-white/10 px-2 rounded hover:bg-white/20"
                  >
                    COPIAR
                  </button>
                </p>
                <p
                  class="text-xs mt-1 font-bold text-yellow-400 bg-yellow-400/10 p-1 rounded inline-block"
                >
                  Titular: BRAYAN TORRES
                </p>
              </div>
            </div>

            <!-- DYNAMIC INPUTS FIELDSET -->
            <div
              v-if="form.payment_method !== 'CASH'"
              class="pt-4 border-t border-white/10 space-y-3 relative z-10"
            >
              <!-- NEW: Bs Amount & Date for VE payments -->
              <div
                v-if="
                  ['PAGO_MOVIL', 'BS_TRANSFER'].includes(form.payment_method)
                "
                class="grid grid-cols-2 gap-3 p-3 bg-white/5 rounded-lg border border-white/5"
              >
                <div
                  class="col-span-2 text-xs font-bold text-gray-400 uppercase text-center border-b border-white/5 pb-1 mb-1"
                >
                  Detalles de Transferencia
                </div>

                <!-- Monto en Bs (Precalculado - No Editable) -->
                <div class="col-span-2 bg-gold-500/10 border border-gold-500/30 rounded-xl p-4 text-center">
                  <label class="block text-[10px] font-bold text-gold-400 uppercase mb-2">
                    💰 MONTO A DEPOSITAR (Bs)
                  </label>
                  <div class="text-3xl font-black text-gold-400 font-mono">
                    {{ form.amount_bs?.toLocaleString('es-VE', {minimumFractionDigits: 2}) }} Bs
                  </div>
                  <p class="text-[10px] text-gray-500 mt-1">Monto exacto a transferir</p>
                </div>
                <!-- Fecha Pago -->
                <div class="col-span-1">
                  <label
                    class="block text-[10px] font-bold text-gray-500 uppercase mb-1"
                    >Fecha Pago</label
                  >
                  <input
                    v-model="form.payment_exact_date"
                    type="date"
                    class="input-field !text-white text-xs"
                  />
                </div>
              </div>

              <!-- 1. Origin Bank (For VE/CO transfers) -->
              <div
                v-if="
                  [
                    'PAGO_MOVIL',
                    'BS_TRANSFER',
                    'BANCOLOMBIA',
                    'NEQUI',
                  ].includes(form.payment_method)
                "
              >
                <label
                  class="block text-xs font-bold text-gray-500 uppercase mb-1"
                >
                  {{
                    ["BANCOLOMBIA", "NEQUI"].includes(form.payment_method)
                      ? "Banco Origen (Colombia)"
                      : "Banco Origen (Venezuela)"
                  }}
                </label>
                <select
                  v-model="form.payment_issuing_bank"
                  class="input-field appearance-none !bg-black !text-white !border-white/20 custom-select"
                >
                  <option value="" disabled selected class="text-gray-500">
                    Selecciona tu banco
                  </option>
                  <option
                    v-for="bank in ['BANCOLOMBIA', 'NEQUI'].includes(
                      form.payment_method
                    )
                      ? COLOMBIA_BANKS
                      : VENEZUELA_BANKS"
                    :key="bank"
                    :value="bank"
                    class="bg-black text-white"
                  >
                    {{ bank }}
                  </option>
                </select>
              </div>

              <!-- 2. Source Email (For Zelle/Binance) -->
              <div v-if="['ZELLE', 'BINANCE'].includes(form.payment_method)">
                <label
                  class="block text-xs font-bold text-gray-500 uppercase mb-1"
                >
                  {{
                    form.payment_method === "ZELLE"
                      ? "Correo del Dueño del Zelle"
                      : "Tu Correo de Binance"
                  }}
                </label>
                <input
                  v-model="form.payment_source_email"
                  type="email"
                  placeholder="ejemplo@correo.com"
                  class="input-field lowercase"
                />
              </div>

              <!-- 3. Source Phone (For Bizum) -->
              <div v-if="form.payment_method === 'BIZUM'">
                <label
                  class="block text-xs font-bold text-gray-500 uppercase mb-1"
                  >Tu Teléfono Móvil</label
                >
                <input
                  v-model="form.payment_source_number"
                  type="tel"
                  placeholder="Número desde donde pagaste"
                  class="input-field"
                />
              </div>

              <!-- 3. Reference (Common) -->
              <div>
                <label
                  class="block text-xs font-bold text-blue-400 uppercase mb-1"
                >
                  {{
                    form.payment_method === "NEQUI"
                      ? "Número de Operación"
                      : "Referencia / Comprobante"
                  }}
                </label>
                <input
                  v-model="form.payment_ref"
                  @input="toUpper('payment_ref')"
                  type="text"
                  placeholder="Últimos dígitos o Ref completa"
                  class="input-field !border-blue-500/30 focus:!border-blue-400"
                />
                <!-- Amount Field for Binance (Optional hint, though logic calculates it) -->
                <p
                  v-if="form.payment_method === 'BINANCE'"
                  class="text-[10px] text-gray-400 mt-1 text-right"
                >
                  Monto exacto a enviar: ${{ totalAmount }} USDT
                </p>
              </div>
            </div>

            <div
              v-else
              class="mt-2 text-xs text-yellow-500 bg-yellow-500/10 p-2 rounded"
            >
              ⚠️ Coordina la entrega del efectivo con tu vendedor asignado.
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="p-6 pt-0 flex gap-3 bg-surface border-t border-white/5 mt-auto"
      >
        <button
          @click="emit('close')"
          class="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:bg-white/5 transition font-medium"
        >
          Cancelar
        </button>
        <button
          @click="handleSubmit"
          :disabled="!isFormValid || loading"
          class="flex-1 py-3 rounded-xl font-bold text-black transition-all bg-gold-500 hover:bg-gold-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(245,158,11,0.3)]"
        >
          {{ loading ? "Procesando..." : "Registrar Pago" }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-field {
  @apply w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-gold-500/50 transition-colors placeholder:text-gray-600;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
</style>
